/**
 * DbItemCard - 卡片项组件
 *
 * 特性：
 * - 支持多种布局模式：grid（网格）、waterfall（瀑布流）、horizontal（水平滚动）、list（列表）
 * - 支持 1-N 列等分布局
 * - 单列时自动切换为横屏布局
 * - 支持圆角、阴影、边框配置
 * - 自动计算间距，保证元素间距与父容器两侧间距一致
 * - 支持点击事件
 */

import React, { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import {
    View,
    ScrollView,
    TouchableOpacity,
    Dimensions,
    ViewStyle,
    LayoutChangeEvent,
    FlatList,
    NativeScrollEvent,
    NativeSyntheticEvent,
    ActivityIndicator,
} from 'react-native';
import {
    DbItemCardProps,
    DbItemCardListProps,
} from './DbItemCard.types';
import {
    styles,
    ITEM_CARD_DEFAULTS,
    SHADOW_CONFIGS,
    calculateCardWidth,
    getCardMarginStyle,
    isRowLastItem,
} from './DbItemCard.styles';
import { SwipeListProvider } from '../DbSwipeAction/SwipeContext';

const { width: SCREEN_WIDTH } = Dimensions.get('window');

// ==================== DbItemCard 单个卡片组件 ====================

type DbItemCardComponent = React.FC<DbItemCardProps> & {
    List: typeof DbItemCardList;
};

const DbItemCardBase: React.FC<DbItemCardProps> = ({
    children,
    layoutMode = 'grid',
    numColumns = ITEM_CARD_DEFAULTS.numColumns,
    itemSpacing = ITEM_CARD_DEFAULTS.itemSpacing,
    rowSpacing,
    sideSpacing = ITEM_CARD_DEFAULTS.sideSpacing,
    index = 0,
    style,
    cardHeight,
    minHeight,
    maxHeight,
    cardWidth: customCardWidth,
    backgroundColor = ITEM_CARD_DEFAULTS.backgroundColor,
    borderRadius = ITEM_CARD_DEFAULTS.borderRadius,
    shadow = true,
    shadowLevel = 'sm',
    borderWidth,
    borderColor,
    padding = ITEM_CARD_DEFAULTS.padding,
    onPress,
    onLongPress,
    disabled = false,
    activeOpacity = ITEM_CARD_DEFAULTS.activeOpacity,
    width: customWidth,
    aspectRatio,
    isFirst: _isFirst,
    isLast: _isLast,
    isRowLast,
}) => {
    // 计算实际行间距
    const actualRowSpacing = rowSpacing ?? itemSpacing;
    
    // 计算卡片宽度
    const cardWidth = useMemo(() => {
        if (customWidth !== undefined) {
            return customWidth;
        }
        if (customCardWidth !== undefined) {
            return customCardWidth;
        }
        if (layoutMode === 'horizontal') {
            return ITEM_CARD_DEFAULTS.cardWidthHorizontal;
        }
        return calculateCardWidth(numColumns, itemSpacing, sideSpacing);
    }, [customWidth, customCardWidth, layoutMode, numColumns, itemSpacing, sideSpacing]);

    // 计算卡片高度
    const calculatedHeight = useMemo(() => {
        if (cardHeight !== undefined) {
            return cardHeight;
        }
        if (aspectRatio !== undefined && typeof cardWidth === 'number') {
            return cardWidth / aspectRatio;
        }
        return ITEM_CARD_DEFAULTS.cardHeight;
    }, [cardHeight, aspectRatio, cardWidth]);

    // 获取阴影样式
    const shadowStyle = useMemo(() => {
        if (!shadow) return SHADOW_CONFIGS.none;
        return SHADOW_CONFIGS[shadowLevel];
    }, [shadow, shadowLevel]);

    // 计算边距
    const marginStyle = useMemo(() => {
        // 如果外部已经指定了是否为行尾
        if (isRowLast !== undefined) {
            return {
                marginRight: isRowLast ? 0 : itemSpacing,
                marginBottom: actualRowSpacing,
            };
        }
        return getCardMarginStyle(index, numColumns, itemSpacing, actualRowSpacing, layoutMode);
    }, [index, numColumns, itemSpacing, actualRowSpacing, layoutMode, isRowLast]);

    // 构建卡片样式
    const cardStyle = useMemo<ViewStyle>(() => ({
        width: cardWidth as ViewStyle['width'],
        height: calculatedHeight,
        minHeight,
        maxHeight,
        backgroundColor,
        borderRadius,
        borderWidth,
        borderColor,
        padding,
        ...shadowStyle,
        ...marginStyle,
    }), [
        cardWidth, calculatedHeight, minHeight, maxHeight,
        backgroundColor, borderRadius, borderWidth, borderColor, padding,
        shadowStyle, marginStyle,
    ]);

    // 渲染内容
    const content = (
        <View style={[styles.card, cardStyle, style]}>
            {children}
        </View>
    );

    // 如果有点击事件，包装成 TouchableOpacity
    if (onPress || onLongPress) {
        return (
            <TouchableOpacity
                onPress={onPress}
                onLongPress={onLongPress}
                activeOpacity={activeOpacity}
                disabled={disabled}
                style={disabled && styles.disabled}
            >
                {content}
            </TouchableOpacity>
        );
    }

    return content;
};

// ==================== DbItemCardList 卡片列表容器组件 ====================

function DbItemCardList<T>({
    data,
    renderItem,
    keyExtractor,
    layoutMode = 'grid',
    numColumns = ITEM_CARD_DEFAULTS.numColumns,
    itemSpacing = ITEM_CARD_DEFAULTS.itemSpacing,
    rowSpacing,
    sideSpacing = ITEM_CARD_DEFAULTS.sideSpacing,
    containerWidth: propContainerWidth,
    borderRadius = ITEM_CARD_DEFAULTS.borderRadius,
    cardBackgroundColor = ITEM_CARD_DEFAULTS.backgroundColor,
    showShadow = true,
    shadowLevel = 'sm',
    cardHeight,
    cardWidth,
    aspectRatio,
    getItemHeight,
    autoMeasureHeight = false,
    defaultCardHeight = ITEM_CARD_DEFAULTS.cardHeight,
    style,
    contentContainerStyle,
    onItemPress,
    ListHeaderComponent,
    ListFooterComponent,
    ListEmptyComponent,
    scrollable = false,
    horizontal: _horizontal = false,
    showsScrollIndicator = false,
    isLoadingMore = false,
    onLoadMore,
    loadMoreThreshold = 500,
    LoadingComponent,
    bottomSpacing = 0,
}: DbItemCardListProps<T>) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _unused = 0; // 占位，避免空函数体报错
    const actualRowSpacing = rowSpacing ?? itemSpacing;
     
    // 自动测量容器宽度
    const [measuredWidth, setMeasuredWidth] = useState<number>(0);
    
    // 自动测量高度相关状态
    const [measuredHeights, setMeasuredHeights] = useState<Map<string, number>>(new Map());
    const [isMeasuring, setIsMeasuring] = useState(false);
    const measureContainerRef = useRef<View>(null);
     
     // 追踪滚动方向和位置，防止重复触发加载更多
     const scrollStateRef = React.useRef({
         lastScrollY: 0,
         lastScrollX: 0,
         isScrollingDown: false,
         isScrollingRight: false,
         hasTriggeredLoadMore: false,
     });
     
     // 当加载完成时，重置触发标志，允许下一次加载
     React.useEffect(() => {
         if (!isLoadingMore) {
             scrollStateRef.current.hasTriggeredLoadMore = false;
         }
     }, [isLoadingMore]);
     
     // 处理容器布局变化
    const handleLayout = useCallback((event: LayoutChangeEvent) => {
        const { width } = event.nativeEvent.layout;
        if (width > 0 && width !== measuredWidth) {
            setMeasuredWidth(width);
        }
    }, [measuredWidth]);
    
    // 使用的容器宽度：优先使用传入的，其次使用测量的，最后使用屏幕宽度
    const containerWidth = propContainerWidth ?? measuredWidth ?? SCREEN_WIDTH;
    
    // 计算卡片宽度（基于实际容器宽度）
    const calculatedCardWidth = useMemo(() => {
        if (cardWidth !== undefined) return cardWidth;
        if (layoutMode === 'horizontal') return ITEM_CARD_DEFAULTS.cardWidthHorizontal;
        // 使用容器宽度计算，而不是屏幕宽度
        return calculateCardWidth(numColumns, itemSpacing, sideSpacing, containerWidth);
    }, [cardWidth, layoutMode, numColumns, itemSpacing, sideSpacing, containerWidth]);
    
    // 判断是否启用自动测量高度
    const shouldAutoMeasure = autoMeasureHeight && layoutMode === 'waterfall' && !getItemHeight;
    
    // 获取项目高度的函数（优先使用传入的 getItemHeight，其次是自动测量的高度，最后是默认高度）
    const getItemHeightInternal = useCallback((item: T, index: number): number => {
        if (getItemHeight) {
            return getItemHeight(item, index);
        }
        if (shouldAutoMeasure) {
            const key = keyExtractor(item, index);
            return measuredHeights.get(key) ?? defaultCardHeight;
        }
        return cardHeight ?? defaultCardHeight;
    }, [getItemHeight, shouldAutoMeasure, measuredHeights, keyExtractor, defaultCardHeight, cardHeight]);
    
    // 测量单个项目高度的回调
    const handleMeasureItemLayout = useCallback((item: T, index: number, event: LayoutChangeEvent) => {
        if (!shouldAutoMeasure) return;
        const { height } = event.nativeEvent.layout;
        const key = keyExtractor(item, index);
        setMeasuredHeights(prev => {
            if (prev.get(key) === height) return prev;
            const next = new Map(prev);
            next.set(key, height);
            return next;
        });
    }, [shouldAutoMeasure, keyExtractor]);
    
    // 当数据变化时，重置测量状态
    useEffect(() => {
        if (shouldAutoMeasure) {
            // 保留已有测量结果，只移除不存在的数据
            setMeasuredHeights(prev => {
                const next = new Map<string, number>();
                data.forEach((item, index) => {
                    const key = keyExtractor(item, index);
                    const height = prev.get(key);
                    if (height !== undefined) {
                        next.set(key, height);
                    }
                });
                return next;
            });
        }
    }, [data, shouldAutoMeasure, keyExtractor]);

    // 渲染单个卡片
    const renderCard = useCallback((item: T, index: number) => {
        const key = keyExtractor(item, index);
        const isFirst = index === 0;
        const isLast = index === data.length - 1;
        
        // horizontal 模式下不使用 isRowLast 逻辑，只有最后一个元素没有右边距
        // grid 模式下，每行最后一个元素没有右边距
        const isRowLast = layoutMode === 'horizontal'
            ? isLast  // horizontal 模式：只有最后一个没有右边距
            : isRowLastItem(index, numColumns);  // grid 模式：每行最后一个没有右边距

        // 获取卡片高度（使用统一的内部函数）
        const itemHeight = layoutMode === 'waterfall'
            ? getItemHeightInternal(item, index)
            : cardHeight;

        return (
            <DbItemCard
                key={key}
                layoutMode={layoutMode}
                numColumns={numColumns}
                itemSpacing={itemSpacing}
                rowSpacing={actualRowSpacing}
                sideSpacing={sideSpacing}
                index={index}
                cardHeight={itemHeight}
                width={calculatedCardWidth}
                backgroundColor={cardBackgroundColor}
                borderRadius={borderRadius}
                shadow={showShadow}
                shadowLevel={shadowLevel}
                aspectRatio={aspectRatio}
                isFirst={isFirst}
                isLast={isLast}
                isRowLast={isRowLast}
                onPress={onItemPress ? () => onItemPress(item, index) : undefined}
            >
                {renderItem(item, index)}
            </DbItemCard>
        );
    }, [
        keyExtractor, numColumns, data.length, cardHeight, getItemHeightInternal,
        layoutMode, itemSpacing, actualRowSpacing, sideSpacing, calculatedCardWidth,
        cardBackgroundColor, borderRadius, showShadow, shadowLevel, aspectRatio,
        onItemPress, renderItem,
    ]);

    // 渲染网格布局
    const renderGridLayout = () => {
        return (
            <View style={[styles.gridContainer, { paddingHorizontal: sideSpacing }]}>
                {data.map((item, index) => renderCard(item, index))}
            </View>
        );
    };

    // 瀑布流数据分配
    const waterfallColumns = useMemo(() => {
        if (layoutMode !== 'waterfall') return null;
        
        const columns: T[][] = Array.from({ length: numColumns }, () => []);
        const columnHeights: number[] = Array(numColumns).fill(0);

        data.forEach((item, index) => {
            const shortestColumnIndex = columnHeights.indexOf(Math.min(...columnHeights));
            columns[shortestColumnIndex].push(item);
            
            const itemHeight = getItemHeightInternal(item, index);
            columnHeights[shortestColumnIndex] += itemHeight + actualRowSpacing;
        });

        return columns;
    }, [data, layoutMode, numColumns, getItemHeightInternal, actualRowSpacing]);

    // 渲染瀑布流布局 - 使用 ScrollView + 两列独立布局实现真正的瀑布流效果
    const renderWaterfallLayout = () => {
        if (!waterfallColumns) return null;

        // 渲染底部加载组件
        const renderFooter = () => {
            if (!isLoadingMore) return (
                <View style={{ height: bottomSpacing }} />
            );
            return (
                <View style={{ paddingVertical: sideSpacing, alignItems: 'center' }}>
                    {LoadingComponent || <ActivityIndicator size="small" color="#999" />}
                </View>
            );
        };

        return (
            <ScrollView
                showsVerticalScrollIndicator={showsScrollIndicator}
                contentContainerStyle={[
                    styles.waterfallScrollContainer,
                    { paddingHorizontal: sideSpacing, paddingBottom: 0 },
                    contentContainerStyle,
                ]}
                onScroll={handleVerticalScroll}
                scrollEventThrottle={16}
            >
                {ListHeaderComponent}
                <View style={styles.waterfallContainer}>
                    {waterfallColumns.map((column, columnIndex) => (
                        <View
                            key={`column-${columnIndex}`}
                            style={[
                                styles.waterfallColumn,
                                { width: calculatedCardWidth },
                                columnIndex < numColumns - 1 && { marginRight: itemSpacing },
                            ]}
                        >
                            {column.map((item, itemIndex) => {
                                const originalIndex = data.indexOf(item);
                                const key = keyExtractor(item, originalIndex);
                                const itemHeight = getItemHeightInternal(item, originalIndex);

                                return (
                                    <DbItemCard
                                        key={key}
                                        layoutMode="waterfall"
                                        numColumns={1}
                                        itemSpacing={0}
                                        rowSpacing={actualRowSpacing}
                                        sideSpacing={0}
                                        index={itemIndex}
                                        cardHeight={itemHeight}
                                        backgroundColor={cardBackgroundColor}
                                        borderRadius={borderRadius}
                                        shadow={showShadow}
                                        shadowLevel={shadowLevel}
                                        width="100%"
                                        onPress={onItemPress ? () => onItemPress(item, originalIndex) : undefined}
                                    >
                                        <View onLayout={(e) => handleMeasureItemLayout(item, originalIndex, e)}>
                                            {renderItem(item, originalIndex)}
                                        </View>
                                    </DbItemCard>
                                );
                            })}
                        </View>
                    ))}
                </View>
                {renderFooter()}
                {ListFooterComponent}
            </ScrollView>
        );
    };

    // 处理水平滚动事件，检测是否需要加载更多
    const handleHorizontalScroll = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
        if (!onLoadMore || isLoadingMore) return;
        
        const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;
        const currentScrollX = contentOffset.x;
        const distanceFromEnd = contentSize.width - layoutMeasurement.width - currentScrollX;
        
        // 判断滚动方向：只有向右滚动时才可能触发加载更多
        const isScrollingRight = currentScrollX > scrollStateRef.current.lastScrollX;
        scrollStateRef.current.lastScrollX = currentScrollX;
        scrollStateRef.current.isScrollingRight = isScrollingRight;
        
        // 当距离右边小于阈值且向右滚动时触发加载更多
        // 使用 hasTriggeredLoadMore 防止在同一次加载过程中重复触发
        if (isScrollingRight && distanceFromEnd < loadMoreThreshold && !scrollStateRef.current.hasTriggeredLoadMore) {
            scrollStateRef.current.hasTriggeredLoadMore = true;
            onLoadMore();
        }
        
        // 当滚动回到距离足够远时，重置触发标志
        if (distanceFromEnd > loadMoreThreshold * 2) {
            scrollStateRef.current.hasTriggeredLoadMore = false;
        }
    }, [onLoadMore, isLoadingMore, loadMoreThreshold]);

    // 处理垂直滚动事件，检测是否需要加载更多（用于 list 和 grid 模式）
    const handleVerticalScroll = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
        if (!onLoadMore || isLoadingMore) return;
        
        const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;
        const currentScrollY = contentOffset.y;
        const distanceFromEnd = contentSize.height - layoutMeasurement.height - currentScrollY;
        
        // 判断滚动方向：只有向下滚动时才可能触发加载更多
        const isScrollingDown = currentScrollY > scrollStateRef.current.lastScrollY;
        scrollStateRef.current.lastScrollY = currentScrollY;
        scrollStateRef.current.isScrollingDown = isScrollingDown;
        
        // 当距离底部小于阈值且向下滚动时触发加载更多
        // 使用 hasTriggeredLoadMore 防止在同一次加载过程中重复触发
        if (isScrollingDown && distanceFromEnd < loadMoreThreshold && !scrollStateRef.current.hasTriggeredLoadMore) {
            scrollStateRef.current.hasTriggeredLoadMore = true;
            onLoadMore();
        }
        
        // 当滚动回到距离足够远时，重置触发标志
        if (distanceFromEnd > loadMoreThreshold * 2) {
            scrollStateRef.current.hasTriggeredLoadMore = false;
        }
    }, [onLoadMore, isLoadingMore, loadMoreThreshold]);

    // 渲染水平滚动布局（使用 FlatList）
    const renderHorizontalLayout = () => {
        return (
            <FlatList
                horizontal
                data={data}
                renderItem={({ item, index }) => renderCard(item, index)}
                keyExtractor={(item, index) => keyExtractor(item, index)}
                showsHorizontalScrollIndicator={showsScrollIndicator}
                contentContainerStyle={[
                    styles.horizontalContainer,
                    {
                        paddingHorizontal: sideSpacing,
                        paddingVertical: 0,
                        paddingBottom: bottomSpacing,
                    },
                    contentContainerStyle,
                ]}
                onScroll={handleHorizontalScroll}
                scrollEventThrottle={16}
                ListFooterComponent={
                    isLoadingMore && LoadingComponent ? (
                        <View style={{ marginRight: sideSpacing }}>
                            {LoadingComponent}
                        </View>
                    ) : null
                }
            />
        );
    };

    // FlatList ref（用于 SwipeListProvider 控制滚动）
    const flatListRef = useRef<FlatList>(null);

    // 渲染列表布局（单列，使用 FlatList + SwipeListProvider 支持内部 SwipeAction 手势协调）
    const renderListLayout = () => {
        return (
            <SwipeListProvider listRef={flatListRef}>
                <FlatList
                    ref={flatListRef}
                    data={data}
                    renderItem={({ item, index }) => {
                        const isLast = index === data.length - 1;
                        return (
                            <DbItemCard
                                layoutMode="list"
                                numColumns={1}
                                itemSpacing={0}
                                rowSpacing={isLast ? 0 : actualRowSpacing}
                                sideSpacing={0}
                                index={index}
                                cardHeight={cardHeight}
                                backgroundColor={cardBackgroundColor}
                                borderRadius={borderRadius}
                                shadow={showShadow}
                                shadowLevel={shadowLevel}
                                width="100%"
                                aspectRatio={aspectRatio}
                                onPress={onItemPress ? () => onItemPress(item, index) : undefined}
                            >
                                {renderItem(item, index)}
                            </DbItemCard>
                        );
                    }}
                    keyExtractor={(item, index) => keyExtractor(item, index)}
                    showsVerticalScrollIndicator={showsScrollIndicator}
                    contentContainerStyle={[
                        styles.listContainer,
                        { paddingHorizontal: sideSpacing, paddingBottom: bottomSpacing },
                        contentContainerStyle,
                    ]}
                    onScroll={handleVerticalScroll}
                    scrollEventThrottle={16}
                    ListFooterComponent={
                        isLoadingMore && LoadingComponent ? (
                            <View style={{ paddingVertical: sideSpacing }}>
                                {LoadingComponent}
                            </View>
                        ) : null
                    }
                />
            </SwipeListProvider>
        );
    };

    // 检查是否需要等待测量
    const needsMeasurement = !propContainerWidth && measuredWidth === 0 && layoutMode !== 'horizontal' && layoutMode !== 'list';
    
    // 渲染内容
    const renderContent = () => {
        // 如果需要测量但还没测量完成，返回空占位
        if (needsMeasurement) {
            return null;
        }
        
        if (data.length === 0 && ListEmptyComponent) {
            return ListEmptyComponent;
        }

        switch (layoutMode) {
            case 'waterfall':
                return renderWaterfallLayout();
            case 'horizontal':
                return renderHorizontalLayout();
            case 'list':
                return renderListLayout();
            case 'grid':
            default:
                return renderGridLayout();
        }
    };

    // list 模式直接返回 FlatList（已经包含滚动功能）
    if (layoutMode === 'list') {
        return renderListLayout();
    }

    // 包装滚动容器
    const content = (
        <>
            {ListHeaderComponent}
            {renderContent()}
            {ListFooterComponent}
        </>
    );

    if (scrollable && layoutMode !== 'horizontal') {
        return (
            <ScrollView
                style={style}
                contentContainerStyle={[styles.scrollContainer, contentContainerStyle]}
                showsVerticalScrollIndicator={showsScrollIndicator}
                onLayout={handleLayout}
                onScroll={handleVerticalScroll}
                scrollEventThrottle={16}
            >
                {content}
            </ScrollView>
        );
    }

    return (
        <View style={style} onLayout={handleLayout}>
            {content}
        </View>
    );
}

// 挂载子组件并导出
export const DbItemCard = DbItemCardBase as DbItemCardComponent;
DbItemCard.List = DbItemCardList;
DbItemCard.displayName = 'DbItemCard';

export default DbItemCard;
export type {
    DbItemCardProps,
    DbItemCardListProps,
    LayoutMode,
    CardSize,
    ShadowConfig,
} from './DbItemCard.types';
